import React, { useEffect, useState, useCallback, useMemo } from "react";
import { getBudgetCategories, getBudget, addBudgetTx, removeBudgetTx } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useI18n } from "../lib/i18n";
import { Plus, X, TrendingUp, TrendingDown, Wallet, PieChart as PieChartIcon } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function Budget() {
    const { t } = useI18n();
    const { format, currency: dispCurrency, currencies } = useCurrency();
    const [categories, setCategories] = useState([]);
    const [month, setMonth] = useState(thisMonth());
    const [data, setData] = useState({ transactions: [], income: 0, expenses: 0, balance: 0, by_category: {} });
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [cats, b] = await Promise.all([getBudgetCategories(), getBudget(month)]);
            setCategories(cats);
            setData(b);
        } catch (e) {
            toast.error("Errore: " + e.message);
        } finally {
            setLoading(false);
        }
    }, [month]);

    useEffect(() => { load(); }, [load]);

    const catById = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);

    // Breakdown rows:
    // - Income rows: % rispetto al totale income
    // - Expense rows: % rispetto allo STIPENDIO (income totale del mese)
    const breakdownRows = useMemo(() => {
        return Object.entries(data.by_category || {})
            .sort(([, a], [, b]) => b - a)
            .map(([catId, amt]) => {
                const c = catById[catId];
                const isInc = c?.type === "income";
                // Expenses are measured against income (stipendio). If no income yet, fall back to total expenses.
                const denom = isInc
                    ? (data.income || 0)
                    : (data.income || data.expenses || 0);
                const pct = denom ? (amt / denom) * 100 : 0;
                return { catId, amt, cat: c, pct, isInc };
            });
    }, [data.by_category, data.income, data.expenses, catById]);

    // Slices used by the pie chart — only EXPENSE categories
    const expensePieData = useMemo(() => {
        return breakdownRows
            .filter(r => !r.isInc && r.amt > 0)
            .map(r => ({
                name: r.cat?.label_it || r.catId,
                value: r.amt,
                color: r.cat?.color || "var(--brand-blue)",
                pct: r.pct,
            }));
    }, [breakdownRows]);

    // Percent of income spent (total). Useful as overall savings rate indicator.
    const expenseRatio = data.income ? (data.expenses / data.income) * 100 : 0;
    const savingsRatio = data.income ? Math.max(0, ((data.income - data.expenses) / data.income) * 100) : 0;

    const handleRemove = async (id) => {
        try {
            await removeBudgetTx(id);
            toast.success("Voce rimossa");
            load();
        } catch (e) { toast.error("Errore: " + e.message); }
    };

    const balanceUp = (data.balance || 0) >= 0;

    return (
        <div data-testid="budget-page" className="px-4 py-6 max-w-[1600px] mx-auto fade-up">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="text-[10px] text-mute tracking-[0.2em] mb-1">PERSONAL BUDGET</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight brand-gradient-text">
                        {t("budget_title_pre")} {t("budget_title_accent")}
                    </h1>
                    <p className="text-dim text-sm mt-2">{t("budget_subtitle")}</p>
                </div>
                <div className="flex items-center gap-3">
                    <input
                        type="month"
                        data-testid="budget-month"
                        value={month}
                        onChange={e => setMonth(e.target.value)}
                        className="bg-elev2 border border-soft px-2 py-1.5 text-sm tnum focus:border-amber outline-none"
                        style={{ colorScheme: "dark" }}
                    />
                    <button
                        data-testid="add-tx-btn"
                        onClick={() => setShowForm(s => !s)}
                        className="border px-4 py-2 text-xs font-semibold tracking-wider hover:bg-elev2 flex items-center gap-2"
                        style={{ color: "var(--amber)", borderColor: "var(--amber)" }}
                    >
                        <Plus size={14} />
                        {showForm ? t("cancel") : t("add_tx")}
                    </button>
                </div>
            </div>

            {showForm && <BudgetForm categories={categories} onSaved={() => { setShowForm(false); load(); }} defaultCurrency={dispCurrency} />}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <StatBox label={t("income")} value={format(data.income)} highlight="green" icon={TrendingUp} />
                <StatBox label={t("expenses")} value={format(data.expenses)} highlight="red" icon={TrendingDown} />
                <StatBox label={t("balance")} value={(balanceUp ? "+" : "") + format(data.balance)} highlight={balanceUp ? "green" : "red"} icon={Wallet} accent />
                <StatBox
                    label="% SPESO / RISPARMIATO"
                    value={data.income ? `${expenseRatio.toFixed(0)}% · ${savingsRatio.toFixed(0)}%` : "—"}
                    highlight={expenseRatio > 80 ? "red" : "green"}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
                {/* Categories breakdown */}
                <div className="lg:col-span-2">
                    <BudgetBreakdown rows={breakdownRows} format={format} emptyLabel={t("no_tx")} hasIncome={!!data.income} />
                </div>
                {/* Expenses pie chart */}
                <BudgetPie data={expensePieData} format={format} totalExpenses={data.expenses} totalIncome={data.income} />
            </div>

            {/* Transactions table */}
            <div data-testid="tx-table" className="border border-soft bg-elev">
                <div className="px-4 py-2.5 text-[10px] tracking-widest text-mute border-b border-soft uppercase">
                    Voci · {data.transactions.length}
                </div>
                {loading && <div className="px-4 py-8 text-center text-mute text-sm">{t("loading")}</div>}
                {!loading && data.transactions.map(tx => (
                    <BudgetTxRow
                        key={tx.id}
                        tx={tx}
                        cat={catById[tx.category]}
                        format={format}
                        dispCurrency={dispCurrency}
                        currencies={currencies}
                        onRemove={handleRemove}
                    />
                ))}
            </div>
        </div>
    );
}

const BudgetBreakdown = React.memo(({ rows, format, emptyLabel, hasIncome }) => (
    <div data-testid="budget-breakdown" className="border border-soft bg-elev p-4 h-full">
        <div className="text-xs text-mute tracking-widest mb-3 flex items-center justify-between">
            <span>BREAKDOWN PER CATEGORIA</span>
            {hasIncome && <span className="text-[9px]" style={{ color: "var(--brand-green)" }}>% calcolata sullo stipendio</span>}
        </div>
        <div className="space-y-2">
            {rows.map(({ catId, amt, cat, pct, isInc }) => (
                <div key={catId} className="flex items-center gap-3 text-xs">
                    <div className="w-32 truncate flex items-center gap-1.5">
                        <span className="w-2 h-2 inline-block" style={{ background: cat?.color }} />
                        {cat?.label_it || catId}
                    </div>
                    <div className="flex-1 h-2 bg-elev2 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0" style={{ width: `${Math.min(pct, 100)}%`, background: cat?.color || "var(--amber)" }} />
                    </div>
                    <div
                        className="w-14 text-right tnum text-[10px]"
                        title={isInc ? "% sul totale entrate" : "% sullo stipendio"}
                        style={{ color: isInc ? "var(--brand-green)" : (pct > 25 ? "var(--red)" : "var(--text-dim)") }}
                    >
                        {pct.toFixed(1)}%
                    </div>
                    <div className={`w-28 text-right tnum font-semibold ${isInc ? "text-green-up" : "text-red-down"}`}>
                        {isInc ? "+" : "-"}{format(amt)}
                    </div>
                </div>
            ))}
            {rows.length === 0 && (
                <div className="text-mute text-xs text-center py-6">{emptyLabel}</div>
            )}
        </div>
    </div>
));
BudgetBreakdown.displayName = "BudgetBreakdown";

// Stable styles to avoid creating new objects on every render.
// Note: Recharts uses 'color' for default text and itemStyle/labelStyle to
// override per-row. We were missing those, which is why the tooltip showed
// a black box with invisible text on dark backgrounds.
const PIE_TOOLTIP_STYLE = {
    background: "#0a0e14",
    border: "1px solid #2a3548",
    fontSize: 11,
    fontFamily: "JetBrains Mono, monospace",
    color: "#e5e7eb",
    padding: "8px 10px",
    borderRadius: 4,
    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
};
const PIE_TOOLTIP_LABEL_STYLE = { color: "#9ca3af", marginBottom: 4, fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase" };
const PIE_TOOLTIP_ITEM_STYLE = { color: "#e5e7eb", padding: "1px 0" };


// Custom tooltip content for the Budget pie:
// Shows: category name + amount (display currency) + % of income (stipendio).
// Replaces the default Recharts tooltip that was rendering as a black box.
function BudgetPieTooltip({ active, payload, format, totalIncome }) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const pctIncome = totalIncome > 0 ? (p.value / totalIncome) * 100 : 0;
    return (
        <div style={PIE_TOOLTIP_STYLE} data-testid="budget-pie-tooltip">
            <div style={PIE_TOOLTIP_LABEL_STYLE}>{p.name}</div>
            <div style={PIE_TOOLTIP_ITEM_STYLE}>
                <span style={{ color: p.color, marginRight: 6 }}>●</span>
                <span className="tnum">{format(p.value)}</span>
            </div>
            <div style={{ ...PIE_TOOLTIP_ITEM_STYLE, color: "#9ca3af" }}>
                <span className="tnum">{p.pct.toFixed(1)}%</span> della spesa
                {totalIncome > 0 && (
                    <> · <span className="tnum">{pctIncome.toFixed(1)}%</span> dello stipendio</>
                )}
            </div>
        </div>
    );
}

const BudgetPie = React.memo(({ data, format, totalExpenses, totalIncome }) => (
    <div data-testid="budget-pie" className="border border-soft bg-elev p-4 h-full flex flex-col">
        <div className="text-xs text-mute tracking-widest mb-2 flex items-center gap-2">
            <PieChartIcon size={12} />
            DISTRIBUZIONE SPESE
        </div>
        {data.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-mute text-xs text-center">
                Aggiungi qualche spesa per vedere il grafico
            </div>
        ) : (
            <>
                <div style={{ width: "100%", height: 240 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={45}
                                outerRadius={85}
                                paddingAngle={2}
                                stroke="#0a0e14"
                                isAnimationActive={false}
                            >
                                {data.map((d) => <Cell key={d.name} fill={d.color} />)}
                            </Pie>
                            <Tooltip
                                content={<BudgetPieTooltip format={format} totalIncome={totalIncome} />}
                                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                <div className="text-center mt-2">
                    <div className="text-[10px] text-mute tracking-wider">TOTALE SPESE</div>
                    <div className="text-lg font-bold tnum" style={{ color: "var(--red)" }}>{format(totalExpenses)}</div>
                    {totalIncome > 0 && (
                        <div className="text-[10px] text-mute mt-0.5">
                            = {((totalExpenses / totalIncome) * 100).toFixed(1)}% dello stipendio
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3 justify-center">
                    {data.slice(0, 6).map((d) => (
                        <div key={d.name} className="flex items-center gap-1 text-[10px]">
                            <span className="w-2 h-2" style={{ background: d.color }} />
                            <span className="text-dim">{d.name}</span>
                        </div>
                    ))}
                </div>
            </>
        )}
    </div>
));
BudgetPie.displayName = "BudgetPie";

const BudgetTxRow = React.memo(({ tx, cat, format, dispCurrency, currencies, onRemove }) => {
    const isInc = cat?.type === "income";
    // If the transaction's input currency matches the user's display currency,
    // show the EXACT amount the user typed (no USD round-trip → no FX drift).
    // Otherwise, render the USD-normalized amount via format() which applies a
    // single, consistent conversion to the display currency.
    const sameCurrency = tx.currency && tx.currency === dispCurrency;
    let amountStr;
    if (sameCurrency) {
        const sym = currencies?.[tx.currency]?.symbol || tx.currency;
        const num = Number(tx.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        amountStr = tx.currency === "DKK" ? `${num} ${sym}` : `${sym}${num}`;
    } else {
        const fallback = tx.amount_usd != null ? tx.amount_usd : tx.amount;
        amountStr = format(fallback);
    }
    return (
        <div data-testid={`tx-${tx.id}`} className="grid grid-cols-12 px-4 py-3 text-sm border-b border-soft items-center gap-2">
            <div className="col-span-2 tnum text-xs text-dim">{tx.date}</div>
            <div className="col-span-3 flex items-center gap-2">
                <span className="w-2 h-2" style={{ background: cat?.color }} />
                <span className="text-xs">{cat?.label_it || tx.category}</span>
            </div>
            <div className="col-span-4 text-xs text-mute truncate">
                {tx.note || "—"}
                {tx.currency && tx.currency !== dispCurrency && (
                    <span className="ml-2 text-[10px] opacity-60">({tx.amount} {tx.currency})</span>
                )}
            </div>
            <div className={`col-span-2 text-right tnum font-semibold ${isInc ? "text-green-up" : "text-red-down"}`}>
                {isInc ? "+" : "-"}{amountStr}
            </div>
            <div className="col-span-1 flex justify-end">
                <button onClick={() => onRemove(tx.id)} className="text-mute hover:text-red-down p-1"><X size={14} /></button>
            </div>
        </div>
    );
});
BudgetTxRow.displayName = "BudgetTxRow";

const StatBox = ({ label, value, highlight, accent, icon: Icon }) => {
    let color;
    if (highlight === "green") color = "var(--green)";
    else if (highlight === "red") color = "var(--red)";
    else if (accent) color = "var(--amber)";
    return (
        <div className="border border-soft bg-elev p-3">
            <div className="flex items-center justify-between text-[10px] text-mute tracking-widest">
                <span>{label}</span>{Icon && <Icon size={12} style={color ? { color } : undefined} />}
            </div>
            <div className="text-2xl font-bold tnum mt-1" style={color ? { color } : undefined}>{value}</div>
        </div>
    );
};

const BudgetForm = ({ categories, onSaved, defaultCurrency }) => {
    const { t } = useI18n();
    const { currencies } = useCurrency();
    const [form, setForm] = useState({
        category: "cibo", amount: "", currency: defaultCurrency, date: todayStr(), note: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.category || !form.amount || !form.date) {
            toast.error("Compila i campi obbligatori"); return;
        }
        setBusy(true);
        try {
            await addBudgetTx({ ...form, amount: parseFloat(form.amount) });
            toast.success("Voce salvata");
            onSaved();
        } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
        finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} data-testid="tx-form" className="border border-amber bg-elev p-4 mb-4 grid grid-cols-1 md:grid-cols-6 gap-3">
            <div>
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("category")} *</label>
                <select
                    data-testid="tx-cat"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                    style={{ colorScheme: "dark" }}
                >
                    {categories.map(c => <option key={c.id} value={c.id}>{c.label_it}</option>)}
                </select>
            </div>
            <div>
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("amount")} *</label>
                <input
                    data-testid="tx-amount"
                    type="number" step="any" min="0"
                    value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                    placeholder="100"
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum focus:border-amber outline-none"
                />
            </div>
            <div>
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("currency")} *</label>
                <select
                    data-testid="tx-currency"
                    value={form.currency}
                    onChange={e => setForm({ ...form, currency: e.target.value })}
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                    style={{ colorScheme: "dark" }}
                >
                    {Object.entries(currencies).map(([code, info]) => <option key={code} value={code}>{info.symbol} {code}</option>)}
                </select>
            </div>
            <div>
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("date")} *</label>
                <input
                    data-testid="tx-date" type="date"
                    value={form.date} max={todayStr()}
                    onChange={e => setForm({ ...form, date: e.target.value })}
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum focus:border-amber outline-none"
                    style={{ colorScheme: "dark" }}
                />
            </div>
            <div className="md:col-span-2">
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("note")}</label>
                <input
                    data-testid="tx-note"
                    value={form.note}
                    onChange={e => setForm({ ...form, note: e.target.value })}
                    placeholder="Opzionale"
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                />
            </div>
            <div className="md:col-span-6 flex justify-end">
                <button
                    type="submit" data-testid="tx-submit" disabled={busy}
                    className="border px-4 py-1.5 text-xs font-semibold tracking-wider disabled:opacity-50"
                    style={{ background: "var(--amber)", color: "#000", borderColor: "var(--amber)" }}
                >
                    {busy ? t("saving") : t("save_tx")}
                </button>
            </div>
        </form>
    );
};
